/**
 * ManageConfig — Admin UI for customising ACTS terminology.
 * Any camp can rename "Camper" → "Scout", "Kerchief" → "Badge",
 * "Achievement Credit" → "Merit", etc. from this page.
 */
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import PageHeader from "../../components/layout/PageHeader";
import { Card, CardHeader } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import { configApi, type OrgConfig } from "../../api/config";
import { useConfig } from "../../context/ConfigContext";

// Group keys for display
const SECTIONS = [
  {
    title: "Organisation",
    keys: ["org.name", "org.shortName", "org.logoUrl"],
    labels: { "org.name": "Organisation Name", "org.shortName": "Short Name / Abbreviation", "org.logoUrl": "Logo URL (optional)" },
  },
  {
    title: "Participants",
    keys: ["terms.participant", "terms.participants", "terms.group", "terms.groups", "terms.grouping", "terms.groupings", "terms.staff", "terms.staffPlural"],
    labels: {
      "terms.participant": "Participant (singular)",
      "terms.participants": "Participants (plural)",
      "terms.group": "Group / Cabin (singular)",
      "terms.groups": "Groups (plural)",
      "terms.grouping": "Division (singular)",
      "terms.groupings": "Divisions (plural)",
      "terms.staff": "Staff Member (singular)",
      "terms.staffPlural": "Staff Members (plural)",
    },
  },
  {
    title: "Achievement System",
    keys: ["terms.activity", "terms.activities", "terms.credit", "terms.credits", "terms.creditShort", "terms.completion", "terms.completions"],
    labels: {
      "terms.activity": "Activity (singular)",
      "terms.activities": "Activities (plural)",
      "terms.credit": "Achievement Credit (singular)",
      "terms.credits": "Achievement Credits (plural)",
      "terms.creditShort": "AC Abbreviation",
      "terms.completion": "AC (singular, e.g. AC)",
      "terms.completions": "ACs (plural, e.g. ACs)",
    },
  },
  {
    title: "Honors & Awards",
    keys: ["terms.honor", "terms.honor.plural", "terms.kerchief", "terms.kerchiefs", "terms.kerchiefReport"],
    labels: {
      "terms.honor": "Honor",
      "terms.honor.plural": "Honors (plural)",
      "terms.kerchief": "Primary Award (e.g. Kerchief, Badge, Ribbon)",
      "terms.kerchiefs": "Primary Awards (plural)",
      "terms.kerchiefReport": "Award Eligibility Report Name",
    },
  },
  {
    title: "Levels & Assessments",
    keys: ["terms.level", "terms.levels", "terms.graduation", "terms.assessment", "terms.assessments", "terms.assessmentTemplate", "terms.progressReport"],
    labels: {
      "terms.level": "Level (singular)",
      "terms.levels": "Levels (plural)",
      "terms.graduation": "Graduating (earning all ACs in an activity)",
      "terms.assessment": "Assessment (singular)",
      "terms.assessments": "Assessments (plural)",
      "terms.assessmentTemplate": "Assessment Template",
      "terms.progressReport": "Progress Report Name",
    },
  },
];

// react-hook-form treats dots as nested paths (e.g. "org.name" → { org: { name } }).
// The backend expects flat dot-notated keys, so we flatten back.
function flatten(obj: Record<string, unknown>, prefix = ""): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(result, flatten(v as Record<string, unknown>, key));
    } else {
      result[key] = String(v ?? "");
    }
  }
  return result;
}

export default function ManageConfig() {
  const { config, refresh } = useConfig();
  const [saving, setSaving] = useState(false);
  const [allConfig, setAllConfig] = useState<OrgConfig>(config);

  useEffect(() => {
    configApi.get().then(setAllConfig);
  }, [config]);

  const { register, handleSubmit, reset } = useForm<OrgConfig>({
    values: allConfig,
  });

  const onSubmit = async (data: OrgConfig) => {
    setSaving(true);
    try {
      await configApi.update(flatten(data));
      toast.success("Display names saved!");
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Display Name Configuration"
      />

      <div className="p-6 lg:p-8">
        <p className="text-sm text-stone-500 mb-6 max-w-2xl">
          Change any term below and click <strong>Save</strong>. All labels in the UI, reports,
          and exports will update immediately. These settings apply to all users.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {SECTIONS.map((section) => (
            <Card key={section.title}>
              <CardHeader title={section.title} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {section.keys.map((key) => (
                  <Input
                    key={key}
                    label={(section.labels as unknown as Record<string, string>)[key] ?? key}
                    placeholder={config[key] ?? key}
                    {...register(key)}
                  />
                ))}
              </div>
            </Card>
          ))}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={() => reset(allConfig)}>
              Reset
            </Button>
            <Button type="submit" variant="primary" loading={saving}>
              Save Display Names
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
